"use client";

import {
  Breadcrumbs,
  ProductCard,
  Header,
  ProductFilters,
  BrandsList,
  SponsoredBanners,
  Button,
  ModalPortal,
} from "@/components";
import Image from "next/image";
import EntryModal from "@/components/modals/EntryModal";
import LogoLoader from "@/components/ui/LogoLoader";
import { getStoredStoreId } from "@/lib/store-storage";
import { PRODUCT_PLACEHOLDER_IMAGE } from "@shared/lib/product-placeholder";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  use,
  type ComponentType,
  type ReactNode,
} from "react";
import { useCart } from "@/components";
import {
  AccessoriesIcon,
  EdiblesIcon,
  ExtractsIcon,
  FlowerIcon,
  MerchIcon,
  PreRollIcon,
  TincturesIcon,
  TopicalsIcon,
  VapesIcon,
  StorefrontIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SortIcon,
} from "@/components/icons";

import type { ProductAvailability, SearchPageParams } from "./types";
import { getSearchBannerSlots } from "./banner-slots";
import { getMobilePaginationItems, getPaginationItems } from "./pagination";
import { SEARCH_SORT_OPTIONS } from "./sort-options";
import { useSearchFilters } from "./useSearchFilters";
import { useSearchPagination } from "./useSearchPagination";
import { useSearchProducts } from "./useSearchProducts";
import { useSearchSortDropdown } from "./useSearchSortDropdown";
import { useStickySearchChrome } from "./useStickySearchChrome";
import { joinQueryParam, toSlug } from "./url-utils";
import { useScrollLock } from "@/lib/use-scroll-lock";
import { resolveSearchHero } from "@/lib/search-hero";

export default function StorePage({
  searchParams,
}: {
  searchParams?: Promise<SearchPageParams>;
}) {
  const base = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";
  const REQUEST_TIMEOUT_MS = 3500;
  const params = searchParams ? use(searchParams) : {};

  const { headerHeight, isDesktop } = useStickySearchChrome();
  const { sortOpen, setSortOpen, sortWrapRef } = useSearchSortDropdown();
  const resultsTopRef = useRef<HTMLDivElement | null>(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const {
    filters,
    setFilters,
    updateFilters,
    clearAllFilters,
    clearLastFilter,
    hasMatchedSlugs,
    setHasMatchedSlugs,
  } = useSearchFilters(params, isDesktop);

  const [modalProduct, setModalProduct] = useState<ProductAvailability | null>(
    null
  );
  useScrollLock(Boolean(modalProduct));
  const [stores, setStores] = useState<
    Array<{
      covaLocationId: number;
      name: string;
      city?: string;
      province?: string;
      address?: string;
    }>
  >([]);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [collectionHero, setCollectionHero] = useState<{
    title: string;
    description: string | null;
    bannerImageUrl: string | null;
  } | null>(null);
  const { setStore, state, isInitialized } = useCart();

  const storedStoreId = getStoredStoreId();
  const covaLocationId = storedStoreId || state.storeCovaLocationId;

  const { data, loading } = useSearchProducts({
    base,
    covaLocationId,
    filters,
    hasMatchedSlugs,
    setHasMatchedSlugs,
    setFilters,
  });

  const { setPage } = useSearchPagination({
    setFilters,
    totalPages: data?.totalPages,
    resultsTopRef,
  });

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    (async () => {
      try {
        const res = await fetch(`${base}/api/stores`, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (res.ok) {
          const json = await res.json();
          if (json?.stores) setStores(json.stores);
        }
      } catch {}
      finally {
        clearTimeout(timeoutId);
      }
    })();
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [base]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!covaLocationId || !Number.isFinite(covaLocationId)) {
      setShowEntryModal(true);
    }
  }, [isInitialized, covaLocationId]);

  useEffect(() => {
    if (!isInitialized) return;
    if (storedStoreId && storedStoreId !== state.storeCovaLocationId) {
      setStore(storedStoreId);
    }
  }, [isInitialized, storedStoreId, state.storeCovaLocationId, setStore]);

  const q = filters.q;
  const categories = filters.categories || [];
  const brands = filters.brands || [];
  const collections = filters.collections || [];
  const subCategories = filters.subCategories || [];
  const sizes = filters.sizes || [];
  const growingMethods = filters.growingMethods || [];
  const types = filters.types || [];
  const strains = filters.strains || [];
  const dominantTerpenes = filters.dominantTerpenes || [];
  const flavors = filters.flavors || [];
  const provincesOfOrigin = filters.provincesOfOrigin || [];
  const regionsOfProduct = filters.regionsOfProduct || [];
  const extractionProcesses = filters.extractionProcesses || [];
  const carrierOils = filters.carrierOils || [];
  const strainType = filters.strainType;
  const onSale = filters.onSale;
  const thcMin = filters.thcMin;
  const thcMax = filters.thcMax;
  const cbdMin = filters.cbdMin;
  const cbdMax = filters.cbdMax;
  const cbnMin = filters.cbnMin;
  const cbnMax = filters.cbnMax;
  const cbcMin = filters.cbcMin;
  const cbcMax = filters.cbcMax;
  const thcvMin = filters.thcvMin;
  const thcvMax = filters.thcvMax;
  const piecesMin = filters.piecesMin;
  const piecesMax = filters.piecesMax;
  const totalTerpeneMin = filters.totalTerpeneMin;
  const totalTerpeneMax = filters.totalTerpeneMax;
  const priceMin = filters.priceMin;
  const priceMax = filters.priceMax;
  const page = filters.page;
  const limit = filters.limit;
  const sortBy = filters.sortBy;
  // Follows the LIVE collection filter (first selected), not just the initial
  // route/query param: the hero appears when a collection is picked in the
  // sidebar and disappears when the collection filter is cleared, keeping
  // /collections/[handle] and /search?collection=… perfectly in sync.
  const activeCollectionHandle = useMemo(() => {
    const first = (filters.collections || [])[0];
    return first ? toSlug(first) : "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(filters.collections || []).join(",")]);

  const sortLabel =
    SEARCH_SORT_OPTIONS.find((o) => o.value === sortBy)?.label || "Recommended";

  const sortMobileLabel =
    SEARCH_SORT_OPTIONS.find((o) => o.value === sortBy)?.mobileLabel ||
    "Recommended";

  const handleMobileFilterOpenChange = useCallback(
    (open: boolean) => {
      setIsMobileFilterOpen(open);
      if (open) setSortOpen(false);
    },
    [setSortOpen]
  );

  const hasActiveFilters =
    categories.length > 0 ||
    brands.length > 0 ||
    collections.length > 0 ||
    subCategories.length > 0 ||
    sizes.length > 0 ||
    growingMethods.length > 0 ||
    types.length > 0 ||
    strains.length > 0 ||
    dominantTerpenes.length > 0 ||
    flavors.length > 0 ||
    provincesOfOrigin.length > 0 ||
    regionsOfProduct.length > 0 ||
    extractionProcesses.length > 0 ||
    carrierOils.length > 0 ||
    Boolean(strainType) ||
    Boolean(onSale) ||
    Boolean(thcMin) ||
    Boolean(thcMax) ||
    Boolean(cbdMin) ||
    Boolean(cbdMax) ||
    Boolean(cbnMin) ||
    Boolean(cbnMax) ||
    Boolean(cbcMin) ||
    Boolean(cbcMax) ||
    Boolean(thcvMin) ||
    Boolean(thcvMax) ||
    Boolean(piecesMin) ||
    Boolean(piecesMax) ||
    Boolean(totalTerpeneMin) ||
    Boolean(totalTerpeneMax) ||
    Boolean(priceMin) ||
    Boolean(priceMax);

  const bannerSlots = useMemo(
    () => getSearchBannerSlots(isDesktop),
    [isDesktop]
  );

  useEffect(() => {
    if (!activeCollectionHandle) {
      setCollectionHero(null);
      return;
    }

    const query = new URLSearchParams();
    if (state.fulfillmentMode === "pickup" && covaLocationId) {
      query.set("locationType", "store");
      query.set("covaLocationId", String(covaLocationId));
    } else if (state.fulfillmentMode === "delivery") {
      query.set("locationType", "same-day");
      if (covaLocationId) query.set("covaLocationId", String(covaLocationId));
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    (async () => {
      try {
        const url = `${base}/api/collections/handle/${encodeURIComponent(activeCollectionHandle)}${
          query.toString() ? `?${query.toString()}` : ""
        }`;
        const res = await fetch(url, { cache: "no-store", signal: controller.signal });
        if (!res.ok) {
          setCollectionHero(null);
          return;
        }
        const json = await res.json();
        if (json?.success && json?.collection) {
          setCollectionHero({
            title: json.collection.title || "Collection",
            description: json.collection.description || null,
            bannerImageUrl: json.collection.bannerImageUrl || null,
          });
        } else {
          setCollectionHero(null);
        }
      } catch {
        if (!controller.signal.aborted) setCollectionHero(null);
      } finally {
        clearTimeout(timeoutId);
      }
    })();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [base, REQUEST_TIMEOUT_MS, covaLocationId, activeCollectionHandle, state.fulfillmentMode]);

  // Show EntryModal if no store ID
  if (showEntryModal) {
    return (
      <div className="min-h-screen">
        <Header stores={stores} base={base} />
        <EntryModal
          stores={stores}
          base={base}
          onClose={() => setShowEntryModal(false)}
        />
      </div>
    );
  }

  // Only show error if we're not loading and there's actually an error
  if (!loading && (!data || !data.success)) {
    return (
      <div className="min-h-screen">
        <Header stores={stores} base={base} />
        <main className="flex min-h-screen items-center justify-center p-6">
          <div className="text-center">
            <h1 className="text-xl font-semibold text-red-600">
              Failed to load products
            </h1>
            {data && !data.success && data.error && (
              <p className="mt-2 text-sm text-text-secondary">{data.error}</p>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Initial load: we don't have filter options yet, so show the brand loader.
  // Subsequent filter changes keep existing data on screen and only replace the product list.
  if (!data) {
    return (
      <div className="min-h-screen">
        <Header stores={stores} base={base} searchQuery={q} />
        <main className="flex min-h-screen items-center justify-center p-6">
          <LogoLoader size="md" label="Loading products…" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <Header stores={stores} base={base} searchQuery={q} />

      {/* Category header + quick category pills */}
      {(() => {
        // Helper function to find exact match in database categories
        // Maps UI display labels to actual database classificationName values (case-insensitive exact match)
        const findExactCategoryMatch = (displayValue: string): string | null => {
          if (!data?.categories || data.categories.length === 0) return null;
          const lowerDisplay = displayValue.toLowerCase().trim();
          
          // Try exact match first (case-insensitive)
          const exactMatch = data.categories.find(c => c.toLowerCase().trim() === lowerDisplay);
          if (exactMatch) return exactMatch;
          
          // Common mapping patterns for variations (e.g., "Flower" vs "Flowers", "Pre-Roll" vs "Pre-Rolls")
          const categoryMap: Record<string, string[]> = {
            'flower': ['flower', 'flowers'],
            'pre-roll': ['pre-roll', 'pre-rolls', 'preroll', 'prerolls', 'pre roll'],
            'extract': ['extract', 'extracts', 'concentrate', 'concentrates'],
            'topical': ['topical', 'topicals'],
            'vaporizer': ['vaporizer', 'vaporizers', 'vape', 'vapes'],
            'edible': ['edible', 'edibles'],
            'tincture': ['tincture', 'tinctures'],
            'accessories': ['accessories', 'accessory'],
            'clothing': ['clothing', 'merch', 'merchandise'],
          };
          
          // Find matching category using the map
          for (const [key, variations] of Object.entries(categoryMap)) {
            if (variations.some(v => v === lowerDisplay)) {
              // Find the actual database value that matches any of the variations
              const match = data.categories.find(c => {
                const lowerC = c.toLowerCase().trim();
                return variations.some(v => lowerC === v || lowerC.includes(v) || v.includes(lowerC));
              });
              if (match) return match;
            }
          }
          
          // Last resort: try to find any category that contains the display value or vice versa
          const fallbackMatch = data.categories.find(c => {
            const lowerC = c.toLowerCase().trim();
            return lowerC.includes(lowerDisplay) || lowerDisplay.includes(lowerC);
          });
          return fallbackMatch || null;
        };

        const hasCollectionHero = Boolean(collectionHero);
        /* Title, description, breadcrumb and cover image all come from one
           resolver so they cannot disagree, and so the precedence
           (collection > category > brand > strain > sale > everything) is
           readable. It also fixes the cover map, which keyed off values the
           catalog does not use: "Vapes" and "Concentrates" alone are ~3,150
           products that were falling back to the generic image. */
        const hero = resolveSearchHero({
          collection: collectionHero,
          categories,
          brands,
          strainType,
          onSale: Boolean(onSale),
        });
        const description = hero.description;

        const coverImageSrc = hero.coverImageSrc;

        // Map display labels to actual database values for exact matching (same as category filter chips)
        const chipDefinitions: Array<{
          key: string;
          label: string;
          displayValue: string;
          Icon: ComponentType<{ size?: number; color?: string; className?: string }>;
        }> = [
          { key: "all", label: "Shop all", displayValue: "", Icon: StorefrontIcon },
          { key: "flowers", label: "Flowers", displayValue: "Flower", Icon: FlowerIcon },
          { key: "prerolls", label: "Pre-rolls", displayValue: "Pre-Roll", Icon: PreRollIcon },
          { key: "extracts", label: "Extracts", displayValue: "Extract", Icon: ExtractsIcon },
          { key: "topicals", label: "Topicals", displayValue: "Topical", Icon: TopicalsIcon },
          { key: "vapes", label: "Vaporizers", displayValue: "Vaporizer", Icon: VapesIcon },
          { key: "edibles", label: "Edibles", displayValue: "Edible", Icon: EdiblesIcon },
          { key: "tinctures", label: "Tinctures", displayValue: "Tincture", Icon: TincturesIcon },
          { key: "accessories", label: "Accessories", displayValue: "Accessories", Icon: AccessoriesIcon },
          { key: "merch", label: "Dank Merch", displayValue: "Clothing", Icon: MerchIcon },
        ];

        // Find the display label for the current category (use exact labels from chipDefinitions)
        const findDisplayLabel = (dbCategoryValue: string): string => {
          // First try exact match on displayValue
          const chip = chipDefinitions.find(def => def.displayValue === dbCategoryValue);
          if (chip) return chip.label;
          // Fallback: try case-insensitive match on displayValue
          const chipLower = chipDefinitions.find(def => 
            def.displayValue.toLowerCase() === dbCategoryValue.toLowerCase()
          );
          if (chipLower) return chipLower.label;
          // Final fallback: return the database value as-is
          return dbCategoryValue;
        };

        // Map chip definitions to actual database values
        const chips = chipDefinitions.map(def => ({
          ...def,
          value: def.displayValue ? findExactCategoryMatch(def.displayValue) : null,
        }));

        const isChipActive = (value: string | null) => {
          if (!value) return categories.length === 0;
          // Check exact match (case-insensitive)
          return categories.some(c => c.toLowerCase() === value.toLowerCase());
        };

        /* Crumb and title both come from the resolver so they agree. Using the
           chip's own wording here instead read "Home > Vaporizers" above a
           heading of "Cannabis Vapes", since the chip is labelled Vaporizers
           while the catalog value (and the main nav) say Vapes. */
        const breadcrumbLabel = hero.breadcrumbLabel;
        const title = hero.title;

        return (
          <div className="px-5 pt-5 space-y-4">
            <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-brand-100">
              {/* Image on right half - desktop only */}
              <div className="hidden lg:block absolute top-0 right-0 w-1/2 h-full">
                <Image
                  src={coverImageSrc}
                  alt=""
                  fill
                  priority={isDesktop}
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  className="object-cover object-right"
                />
                {/* Linear gradient overlay on right half only - desktop only */}
                <div 
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(180deg, rgba(23, 22, 90, 0) 0%, #17165A 111.31%)'
                  }}
                />
              </div>
              {/* Full image on mobile */}
              <div className="lg:hidden absolute inset-0">
                <Image
                  src={coverImageSrc}
                  alt=""
                  fill
                  priority={!isDesktop}
                  sizes="100vw"
                  className="object-cover object-right"
                />
                <div className="absolute inset-0 bg-brand-100/80" />
              </div>
              <div className="relative p-4 lg:p-6">
                {/* Breadcrumb Navigation */}
                <Breadcrumbs
                  items={
                    hasCollectionHero
                      ? [
                          { label: "Collections", href: "/collections" },
                          { label: collectionHero?.title || activeCollectionHandle || "Collection" },
                        ]
                      : breadcrumbLabel
                        ? [{ label: "Shop", href: "/search" }, { label: breadcrumbLabel }]
                        : [{ label: "Shop" }]
                  }
                  tone="onDark"
                  className="mb-0"
                />
                <h1 className="mt-3 text-h3-mobile lg:text-h3-desktop font-bold text-constant-white">
                  {title}
                </h1>
                <div className="mt-2 max-w-3xl text-body-14-mobile lg:text-body-16 font-medium text-constant-white/80">
                  {description}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
              {chips.filter(chip => chip.value || chip.key === "all").map((chip) => {
                const active = isChipActive(chip.value);
                return (
                  <Button
                    key={chip.key}
                    variant={active ? "solid" : "bordered"}
                    color={active ? "primary" : "white"}
                    size="lg"
                    startIcon={<chip.Icon size={18} color="currentColor" className="flex-shrink-0" />}
                    onClick={() => {
                      if (chip.key === "all" || !chip.value) {
                        updateFilters({ categories: [] });
                      } else {
                        updateFilters({ categories: active ? [] : [chip.value] });
                      }
                    }}
                    className="flex-shrink-0 rounded-xl px-3 py-2.5 whitespace-nowrap"
                  >
                    {chip.label}
                  </Button>
                );
              })}
            </div>

            {/* Results count + sort (Desktop only) */}
            <div className="hidden lg:flex items-center justify-between gap-4 flex-wrap">
              <div className="text-h4-mobile lg:text-h4-desktop font-bold text-text-primary">
                {data.total} products found
              </div>

              <div className={`relative ${sortOpen ? "z-50" : ""}`} ref={sortWrapRef}>
                <Button
                  raw
                  type="button"
                  onClick={() => setSortOpen((v) => !v)}
                  data-no-lift
                  className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium select-none transition-colors duration-200 ease-out focus:outline-none ${
                    sortOpen
                      ? "border-blue-10 bg-blue-5 text-blue-100"
                      : "border-neutral-10 bg-constant-white text-text-secondary hover:border-blue-10 hover:bg-blue-5"
                  }`}
                >
                  <SortIcon size={16} color="currentColor" className="flex-shrink-0" />
                  <span className="whitespace-nowrap">
                    <span className={`${sortOpen ? "text-blue-60" : "text-text-tertiary"} font-normal`}>Sort:</span>{""}
                    <span className={`font-semibold ${sortOpen ? "text-blue-100" : "text-text-primary"}`}>{sortLabel}</span>
                  </span>
                  <ChevronDownIcon
                    size={16}
                    color="currentColor"
                    className={`flex-shrink-0 transition-transform ${sortOpen ? "rotate-180" : ""}`}
                  />
                </Button>

                {sortOpen && (
                  <div className="absolute right-0 z-50 mt-1.5 w-56 overflow-hidden rounded-xl border border-neutral-10 bg-constant-white p-1.5 shadow-xl">
                    {SEARCH_SORT_OPTIONS.map((opt) => {
                      const active = opt.value === sortBy;
                      return (
                        <Button
                          key={opt.value}
                          variant="text"
                          color="primary"
                          size="md"
                          onClick={() => { updateFilters({ sortBy: opt.value }); setSortOpen(false); }}
                          className={`w-full !justify-start rounded-lg px-3 py-2.5 text-left text-sm ${
                            active
                              ? "bg-blue-5 text-blue-100 font-semibold"
                              : "font-medium"
                          }`}
                        >
                          {opt.label}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Results count (Mobile only) */}
            <div className="lg:hidden mb-4">
              <div className="text-h4-mobile font-bold text-text-primary">
                {data.total} products found
              </div>
            </div>
          </div>
        );
      })()}

      <main className="flex p-4 gap-4" >
        {/* Left Sidebar - Filters */}
        <aside className="hidden lg:block max-w-[320px] max-[1440px]:max-w-[280px] border bg-constant-white shrink-0 flex-none p-3 rounded-xl sticky top-[calc(var(--header-height)+16px)] self-start">
          <ProductFilters
            mode="desktop"
            searchQuery={q}
            options={{
              categories: data.facets?.categories || data.categories,
              brands: data.facets?.brands || data.brands,
              collections: data.collections,
              subCategories: data.facets?.subCategories,
              sizes: data.facets?.sizes,
              growingMethods: data.facets?.growingMethods,
              types: data.facets?.types,
              strains: data.facets?.strains || data.strains,
              dominantTerpenes: data.facets?.dominantTerpenes || data.terpenes,
              flavors: data.facets?.flavors,
              provincesOfOrigin: data.facets?.provincesOfOrigin,
              regionsOfProduct: data.facets?.regionsOfProduct,
              extractionProcesses: data.facets?.extractionProcesses,
              carrierOils: data.facets?.carrierOils,
              priceMax: Math.max(
                10,
                Math.ceil(Math.max(0, ...Object.values(data.priceMap || {})))
              ),
              thcMax: data.facets?.ranges?.thcMax,
              cbdMax: data.facets?.ranges?.cbdMax,
              cbnMax: data.facets?.ranges?.cbnMax,
              cbcMax: data.facets?.ranges?.cbcMax,
              thcvMax: data.facets?.ranges?.thcvMax,
              piecesMax: data.facets?.ranges?.piecesMax,
              totalTerpeneMax: data.facets?.ranges?.totalTerpeneMax,
            }}
            currentFilters={{
              categories: categories.length ? categories : undefined,
              brands: brands.length ? brands : undefined,
              collections: collections.length ? collections : undefined,
              subCategories: subCategories.length ? subCategories : undefined,
              sizes: sizes.length ? sizes : undefined,
              growingMethods: growingMethods.length ? growingMethods : undefined,
              types: types.length ? types : undefined,
              strains: strains.length ? strains : undefined,
              dominantTerpenes: dominantTerpenes.length ? dominantTerpenes : undefined,
              flavors: flavors.length ? flavors : undefined,
              provincesOfOrigin: provincesOfOrigin.length ? provincesOfOrigin : undefined,
              regionsOfProduct: regionsOfProduct.length ? regionsOfProduct : undefined,
              extractionProcesses: extractionProcesses.length ? extractionProcesses : undefined,
              carrierOils: carrierOils.length ? carrierOils : undefined,
              strainType: strainType || undefined,
              onSale: onSale || undefined,
              thcMin: thcMin || undefined,
              thcMax: thcMax || undefined,
              cbdMin: cbdMin || undefined,
              cbdMax: cbdMax || undefined,
              cbnMin: cbnMin || undefined,
              cbnMax: cbnMax || undefined,
              cbcMin: cbcMin || undefined,
              cbcMax: cbcMax || undefined,
              thcvMin: thcvMin || undefined,
              thcvMax: thcvMax || undefined,
              piecesMin: piecesMin || undefined,
              piecesMax: piecesMax || undefined,
              totalTerpeneMin: totalTerpeneMin || undefined,
              totalTerpeneMax: totalTerpeneMax || undefined,
              priceMin: priceMin || undefined,
              priceMax: priceMax || undefined,
            }}
            onFilterChange={updateFilters}
            onClearFilters={clearAllFilters}
          />
        </aside>

        {/* Main Content - Products Grid */}
        <div className="flex-1 min-w-0">
          <div className="">
            {/* Scroll anchor for pagination (keeps results ~200px from top) */}
            <div ref={resultsTopRef} />
            
            {/* Mobile: Sticky Filters and Sort Row - Positioned below header */}
            <div 
              className="lg:hidden sticky z-[31] -mx-4 min-h-[72px] px-4 sm:-mx-5 sm:px-5 shadow-white shadow-lg mb-4 bg-constant-white"
              style={{ top: 0 }}
            >
              <div className="flex items-center gap-3 py-3">
                <ProductFilters
                  mode="mobile"
                  searchQuery={q}
                  options={{
                    categories: data.facets?.categories || data.categories,
                    brands: data.facets?.brands || data.brands,
                    collections: data.collections,
                    subCategories: data.facets?.subCategories,
                    sizes: data.facets?.sizes,
                    growingMethods: data.facets?.growingMethods,
                    types: data.facets?.types,
                    strains: data.facets?.strains || data.strains,
                    dominantTerpenes: data.facets?.dominantTerpenes || data.terpenes,
                    flavors: data.facets?.flavors,
                    provincesOfOrigin: data.facets?.provincesOfOrigin,
                    regionsOfProduct: data.facets?.regionsOfProduct,
                    extractionProcesses: data.facets?.extractionProcesses,
                    carrierOils: data.facets?.carrierOils,
                    priceMax: Math.max(
                      10,
                      Math.ceil(
                        Math.max(0, ...Object.values(data.priceMap || {}))
                      )
                    ),
                    thcMax: data.facets?.ranges?.thcMax,
                    cbdMax: data.facets?.ranges?.cbdMax,
                    cbnMax: data.facets?.ranges?.cbnMax,
                    cbcMax: data.facets?.ranges?.cbcMax,
                    thcvMax: data.facets?.ranges?.thcvMax,
                    piecesMax: data.facets?.ranges?.piecesMax,
                    totalTerpeneMax: data.facets?.ranges?.totalTerpeneMax,
                  }}
                  currentFilters={{
                    q: q || undefined,
                    categories: categories.length ? categories : undefined,
                    brands: brands.length ? brands : undefined,
                    collections: collections.length ? collections : undefined,
                    subCategories: subCategories.length ? subCategories : undefined,
                    sizes: sizes.length ? sizes : undefined,
                    growingMethods: growingMethods.length ? growingMethods : undefined,
                    types: types.length ? types : undefined,
                    strains: strains.length ? strains : undefined,
                    dominantTerpenes: dominantTerpenes.length ? dominantTerpenes : undefined,
                    flavors: flavors.length ? flavors : undefined,
                    provincesOfOrigin: provincesOfOrigin.length ? provincesOfOrigin : undefined,
                    regionsOfProduct: regionsOfProduct.length ? regionsOfProduct : undefined,
                    extractionProcesses: extractionProcesses.length ? extractionProcesses : undefined,
                    carrierOils: carrierOils.length ? carrierOils : undefined,
                    strainType: strainType || undefined,
              onSale: onSale || undefined,
                    thcMin: thcMin || undefined,
                    thcMax: thcMax || undefined,
                    cbdMin: cbdMin || undefined,
                    cbdMax: cbdMax || undefined,
                    cbnMin: cbnMin || undefined,
                    cbnMax: cbnMax || undefined,
                    cbcMin: cbcMin || undefined,
                    cbcMax: cbcMax || undefined,
                    thcvMin: thcvMin || undefined,
                    thcvMax: thcvMax || undefined,
                    piecesMin: piecesMin || undefined,
                    piecesMax: piecesMax || undefined,
                    totalTerpeneMin: totalTerpeneMin || undefined,
                    totalTerpeneMax: totalTerpeneMax || undefined,
                    priceMin: priceMin || undefined,
                    priceMax: priceMax || undefined,
                  }}
                  onFilterChange={updateFilters}
                  onClearFilters={clearAllFilters}
                  onMobileModalOpenChange={handleMobileFilterOpenChange}
                />
                
                {/* Sort Dropdown */}
                <div className={`relative flex-1 ${sortOpen && !isMobileFilterOpen ? "z-50" : ""}`}>
                  <Button
                    raw
                    type="button"
                    disabled={isMobileFilterOpen}
                    onClick={() => setSortOpen((v) => !v)}
                    data-no-lift
                    className={`w-full inline-flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium select-none transition-colors duration-200 ease-out focus:outline-none ${
                      sortOpen
                        ? "border-blue-10 bg-blue-5 text-blue-100"
                        : "border-neutral-10 bg-constant-white text-text-secondary hover:border-blue-10 hover:bg-blue-5"
                    }`}
                  >
                    <SortIcon size={18} color="currentColor" className="flex-shrink-0" />
                    <span className="flex-1 text-left whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
                      <span className={`${sortOpen ? "text-blue-60" : "text-text-tertiary"} font-normal`}>Sort:</span>{""}
                      <span className={`font-semibold ${sortOpen ? "text-blue-100" : "text-text-primary"}`}>{sortMobileLabel}</span>
                    </span>
                    <ChevronDownIcon
                      size={16}
                      color="currentColor"
                      className={`flex-shrink-0 transition-transform ${sortOpen ? "rotate-180" : ""}`}
                    />
                  </Button>

                  {sortOpen && !isMobileFilterOpen && (
                    <div className="absolute right-0 z-50 mt-1.5 w-64 rounded-xl border border-neutral-10 bg-constant-white p-1.5 shadow-lg">
                      {SEARCH_SORT_OPTIONS.map((opt) => {
                        const active = opt.value === sortBy;
                        return (
                          <Button
                            key={opt.value}
                            variant="text"
                            color="primary"
                            size="md"
                            onClick={() => {
                              updateFilters({ sortBy: opt.value });
                              setSortOpen(false);
                            }}
                            className={`w-full !justify-start text-left rounded-lg px-3 py-2.5 text-sm font-medium ${
                              active
                                ? "bg-blue-5 text-blue-100 font-semibold"
                                : ""
                            }`}
                          >
                            {opt.label}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Product Grid - 4 columns with gap-2 spacing */}
            {loading && data?.success ? (
              <div className="bg-constant-white border border-neutral-1 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-4 divide-x divide-y divide-neutral-10">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="p-4 space-y-3 animate-pulse">
                      <div className="aspect-square bg-bg-2 rounded-xl" />
                      <div className="h-3 bg-bg-2 rounded-full w-3/4" />
                      <div className="h-3 bg-bg-2 rounded-full w-1/2" />
                      <div className="h-8 bg-bg-2 rounded-xl" />
                    </div>
                  ))}
                </div>
              </div>
            ) : data.items.length > 0 ? (
              <div className="bg-constant-white border border-neutral-1 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-4 divide-neutral-10">
                {(() => {
                  // Order comes from the API (full result set sorted before page/limit)
                  const filteredItems = data.items.filter((item) => {
                    // If item has sizeOptions, check if any are available (inStock >= 1)
                    if (item.sizeOptions && item.sizeOptions.length > 0) {
                      const availableOptions = item.sizeOptions.filter(
                        (opt) => opt.inStock >= 1
                      );
                      return availableOptions.length > 0; // Only show if at least one size is available
                    }
                    // If no sizeOptions, check main item stock (backend already applies stock-1)
                    return item.inStock >= 1;
                  });

                  const nodes: ReactNode[] = [];
                  for (let i = 0; i < filteredItems.length; i++) {
                    const item = filteredItems[i];
                    const price = data.priceMap[item.productId];
                    const filteredItem = {
                      ...item,
                      sizeOptions: item.sizeOptions
                        ? item.sizeOptions.filter((opt) => opt.inStock >= 1)
                        : undefined,
                    };

                    nodes.push(
                      <ProductCard
                        key={item.id}
                        item={filteredItem}
                        price={typeof price === "number" ? price : 0}
                        onQuickView={setModalProduct}
                      />
                    );

                    const count = i + 1;
                    const slot = bannerSlots.find((s) => s.after === count);
                    if (slot) {
                      nodes.push(
                        <div
                          key={`banner-${slot.position}`}
                          className="col-span-full px-4 py-4 bg-constant-white"
                        >
                          <SponsoredBanners
                            position={slot.position}
                            limit={1}
                            className="w-full"
                            covaLocationId={covaLocationId}
                          />
                        </div>
                      );
                    }
                  }

                  return nodes;
                })()}
              </div>
              </div>
            ) : (
              <div className="rounded-xl border border-neutral-10 bg-constant-white px-6 py-16 sm:py-20 flex flex-col items-center text-center">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-5">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#3D5AB7"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                </div>
                <h2 className="mb-2 text-body-18 font-bold text-blue-80">
                  No products match these filters
                </h2>
                <p className="mb-6 max-w-md text-body-14 text-text-tertiary">
                  Try removing Last Selected Filter or Clear All
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {(hasActiveFilters || Boolean(q)) && (
                    <Button
                      variant="bordered"
                      color="primary"
                      size="md"
                      onClick={clearLastFilter}
                      className="rounded-xl px-5"
                    >
                      Clear last filter
                    </Button>
                  )}
                  {(hasActiveFilters || Boolean(q)) && (
                    <Button
                      variant="solid"
                      color="primary"
                      size="md"
                      onClick={clearAllFilters}
                      className="rounded-xl px-6"
                    >
                      Clear all
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="sm:mt-8 mt-4">
                {/* Mobile: Combined Show more + Pagination in one container */}
                <div className="lg:hidden rounded-2xl border border-neutral-10 bg-constant-white p-4 space-y-4">
                  {/* Show more button */}
                  <Button
                    variant="bordered"
                    color="white"
                    size="lg"
                    onClick={() => setPage(Math.min(page + 1, data.totalPages))}
                    disabled={page >= data.totalPages}
                    className="w-full rounded-xl"
                  >
                    Show more
                  </Button>

                  {/* Pagination controls */}
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="icon"
                      color="white"
                      size="icon-sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page <= 1}
                      className="h-9 w-9 rounded-xl"
                    >
                      <ChevronLeftIcon size={18} color="currentColor" />
                    </Button>

                    {getMobilePaginationItems(page, data.totalPages).map((pageNum) => {
                      const active = pageNum === page;
                      return (
                        <Button
                          key={pageNum}
                          variant={active ? "solid" : "bordered"}
                          color={active ? "primary" : "white"}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                          className="h-9 min-w-9 px-2.5 rounded-xl text-body-16 font-bold"
                        >
                          {String(pageNum)}
                        </Button>
                      );
                    })}

                    <Button
                      variant="icon"
                      color="white"
                      size="icon-sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= data.totalPages}
                      className="h-9 w-9 rounded-xl"
                    >
                      <ChevronRightIcon size={18} color="currentColor" />
                    </Button>
                  </div>
                </div>

                {/* Desktop: Original layout */}
                <div className="hidden lg:block rounded-2xl border border-neutral-10 bg-constant-white p-2 w-max mx-auto">
                  <div className="flex items-center gap-14">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="icon"
                        color="white"
                        size="icon-md"
                        onClick={() => setPage(page - 1)}
                        disabled={page <= 1}
                        className="h-10 w-10 rounded-xl"
                      >
                        <ChevronLeftIcon size={18} color="currentColor" />
                      </Button>

                      {getPaginationItems(page, data.totalPages).map((it, idx) => {
                        if (it === "…") {
                          return (
                            <div
                              key={`dots-${idx}`}
                                className="h-10 px-3 inline-flex items-center justify-center text-body-16 font-bold text-text-tertiary"
                            >
                              …
                            </div>
                          );
                        }
                        const pageNum = it as number;
                        const active = pageNum === page;
                        return (
                          <Button
                            key={pageNum}
                            variant={active ? "solid" : "bordered"}
                            color={active ? "primary" : "white"}
                            size="md"
                            onClick={() => setPage(pageNum)}
                            className="h-10 min-w-10 px-3 rounded-xl text-body-16 font-bold"
                          >
                            {String(pageNum)}
                          </Button>
                        );
                      })}

                      <Button
                        variant="icon"
                        color="white"
                        size="icon-md"
                        onClick={() => setPage(page + 1)}
                        disabled={page >= data.totalPages}
                        className="h-10 w-10 rounded-xl"
                      >
                        <ChevronRightIcon size={18} color="currentColor" />
                      </Button>
                    </div>

                    {/* Desktop: Show more on the right */}
                    <div className="ml-auto">
                      <Button
                        variant="bordered"
                        color="white"
                        size="md"
                        onClick={() => setPage(Math.min(page + 1, data.totalPages))}
                        disabled={page >= data.totalPages}
                        className="h-10 rounded-2xl px-4 text-body-14"
                      >
                        Show more
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Floating cart drawer button */}
        {modalProduct && (
          <ModalPortal>
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#07063CBF] p-4 !mt-0"
            onClick={() => setModalProduct(null)}
          >
            <div
              className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-lg p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">
                  {modalProduct.product.name}
                </h3>
                <Button
                  type="button"
                  variant="text"
                  color="primary"
                  size="sm"
                  className="rounded"
                  onClick={() => setModalProduct(null)}
                >
                  Close
                </Button>
              </div>
              <div className="mt-3">
                <img
                  src={modalProduct.product.heroShotUri || PRODUCT_PLACEHOLDER_IMAGE}
                  alt="Product"
                  className="h-40 w-40 rounded object-cover bg-bg-2"
                  onError={(e) => {
                    const target = e.currentTarget;
                    if (!target.src.startsWith("data:image/svg+xml")) {
                      target.src = PRODUCT_PLACEHOLDER_IMAGE;
                      target.className = "h-40 w-40 rounded object-contain bg-bg-2 p-4 opacity-60";
                    }
                  }}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                />
              </div>
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div className="text-sm text-text-tertiary">Product ID</div>
                  <div className="font-mono text-sm">
                    {modalProduct.productId}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-text-tertiary">Slug</div>
                  <div className="text-sm">
                    {modalProduct.product.slug || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-text-tertiary">Category</div>
                  <div className="text-sm">
                    {modalProduct.product.classificationName || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-text-tertiary">Catalog SKU</div>
                  <div className="text-sm">
                    {modalProduct.product.catalogSku || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-text-tertiary">Vendor SKU</div>
                  <div className="text-sm">
                    {modalProduct.product.vendorSku || "-"}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-sm text-text-tertiary">
                    Short Description
                  </div>
                  <div className="text-sm">
                    {modalProduct.product.shortDescription || "-"}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-sm text-text-tertiary">
                    Long Description
                  </div>
                  <div className="text-sm">
                    {modalProduct.product.longDescription || "-"}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-sm text-text-tertiary">UPCs</div>
                  <div className="text-sm">
                    {(modalProduct.product.upcs || [])
                      .map((u) => u.upc)
                      .join(",") || "-"}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-sm text-text-tertiary">
                    Specifications
                  </div>
                  <div className="text-sm">
                    {(modalProduct.product.specifications || [])
                      .map((s) => `${s.name}: ${s.value}`)
                      .join(";") || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>
          </ModalPortal>
        )}
      </main>
    </div>
  );
}

// removed wrapper; page is a client component so CartDrawer can be used directly
